import { injectable } from 'inversify';
import { computed, observable, action, runInAction } from 'mobx';
import {
    EstimatesApiService,
    EstimateBundle,
    FinancingAPIService,
    LoansAPIService,
    CalculatedProgram,
    Estimate,
} from 'src/api';
import { ClientDataStore } from 'src/core/stores/client-data.store';
import { RouterStore } from 'src/core/stores/router.store';
import axios from 'axios';

const placeholderImages = ['light-bulb', 'box', 'mountain', 'directions', 'target', 'dial'];

export const enum PriceOptions {
    Show,
    Hide,
    HideLineItem,
}

export const enum PresentationSettingsState {
    LeadWithMonthlyPrice,
    LeadWithTotalPrice,
    OnlyDisplayMonthlyPrice,
    OnlyDisplayTotalPrice,
}

export interface Calculation {
    hasPromo: boolean;
    programName: string;
    loanAmount: number;
    defaultRate: number;
    defaultMonthlyPayment: number;
    defaultTerms: number;
    promotionalRate: number;
    promotionalMonthlyPayment: number;
    promotionalTerms: number;
    message: string;
}

export enum PresentationType {
    NotSet = 'notSet',
    LeadWithMonthlyPrice = 'leadWithMonthlyPrice',
    LeadWithTotalPrice = 'leadWithTotalPrice',
    OnlyDisplayMonthlyPrice = 'onlyDisplayMonthlyPrice',
    OnlyDisplayTotalPrice = 'onlyDisplayTotalPrice',
}

export interface TenantSettings {
    trainingMode: boolean;
    presentationType: PresentationType;
    allowedOnlineEstimatesProviders?: string[] | null;
    hideIneligiblePlans: boolean;
}

@injectable()
export class EstimateStore {
    private bundleId: string | undefined;

    @observable isSignatureSubmitting = false;
    @observable calculations = new Map<string, CalculatedProgram>();
    @observable calculationCard?: CalculatedProgram | undefined = undefined;
    @computed get estimateBundle() {
        return this.estimatesApi.getEstimateBundleObservable;
    }

    constructor(
        private estimatesApi: EstimatesApiService,
        private clientData: ClientDataStore,
        private router: RouterStore,
        private loansApi: LoansAPIService
    ) {}

    getEstimateBundle(bundleId: string): Promise<EstimateBundle> {
        this.bundleId = bundleId;
        return this.estimatesApi.getEstimateBundle(bundleId, true).then((resp) => {
            const bundle = resp.data;
            this.ensureItemsHaveImages(bundle);
            return bundle;
        });
    }

    async getFinancingSettings(tenantId: string): Promise<TenantSettings> {
        if (!tenantId) {
            throw new Error(
                'Required parameter tenantId was null or undefined when calling getFinancingSettings.'
            );
        }
        let settings: TenantSettings = {
            allowedOnlineEstimatesProviders: [],
            presentationType: PresentationType.NotSet,
            trainingMode: false,
            hideIneligiblePlans: false,
        };

        try {
            const result = await axios.get<TenantSettings>(
                `/api/financing/v1/tenants/${tenantId}/settings`
            );
            settings = result.data;
        } catch (error) {
            console.error('Error while getting Financing Settings:', error);
        }

        return settings;
    }

    mapPresentationType(presentationType: PresentationType): PresentationSettingsState | undefined {
        switch (presentationType) {
            case PresentationType.LeadWithMonthlyPrice:
                return PresentationSettingsState.LeadWithMonthlyPrice;
            case PresentationType.LeadWithTotalPrice:
                return PresentationSettingsState.LeadWithTotalPrice;
            case PresentationType.OnlyDisplayMonthlyPrice:
                return PresentationSettingsState.OnlyDisplayMonthlyPrice;
            case PresentationType.OnlyDisplayTotalPrice:
                return PresentationSettingsState.OnlyDisplayTotalPrice;
            default:
                return undefined;
        }
    }

    private ensureItemsHaveImages(bundle: EstimateBundle) {
        bundle.estimates.forEach((estimate) => {
            let index = 0;
            estimate.items.forEach((item) => {
                if (!item.imageUris || item.imageUris.length === 0) {
                    item.imageUris = [`assets/images/placeholder-${placeholderImages[index]}.svg`];
                    index = (index + 1) % placeholderImages.length;
                }
            });
        });
    }

    gotoEstimate = (estimateId: string, bundleId?: string, replace?: boolean) => {
        const go = replace ? this.router.history.replace : this.router.history.push;
        go(`/estimate/${bundleId || this.bundleId}/${estimateId}`);
    };

    @action
    setCalculation = (
        allPlans: boolean,
        totalPrice: number,
        stateCode: string,
        tenantId: string,
        businessUnitId: string
    ) => {
        const key = `${stateCode}_${totalPrice}`;
        if (this.calculations.get(key)) {
            return;
        } else {
            this.loansApi
                .calculate(totalPrice, stateCode, tenantId, businessUnitId, allPlans)
                .then((response) => {
                    const loan = response?.data
                        ? response.data.find((c) => c.eligibleForApplication)
                        : null;

                    if (loan)
                        runInAction(() => {
                            this.calculations.set(key, loan);
                        });
                });
        }
    };

    @action
    openCalculationCard = (calculation: CalculatedProgram | undefined) => {
        this.calculationCard = calculation;
    };

    @action
    closeCalculationCard = () => {
        this.calculationCard = undefined;
    };

    acceptEstimate(
        estimateId: string,
        signatureText?: string,
        signatureImage?: string
    ): Promise<void> {
        const bundleId = this.bundleId;
        if (!bundleId) {
            return Promise.reject('Missing bundle ID');
        }

        return this.estimatesApi
            .acceptEstimate({
                bundleId,
                estimateId,
                signatureImage,
                signatureText,
                notificationEmail: this.clientData.loginData.email,
            })
            .then((resp) => resp.data);
    }

    gotoTerms = (ev: React.MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        this.router.history.push(`${this.router.location.pathname}/terms`);
    };

    @action
    setSignatureSubmitting = (value: boolean) => {
        this.isSignatureSubmitting = value;
    };

    get customerEmail(): string {
        return this.clientData.loginData.email;
    }
}
